import { PlatformUtil } from './platform-utils.js';
import { KeyHelper } from './key-helper.js';
import { KeyCode } from './key-code.js';

/**
 * 用于管理全局快捷键的服务
 * @public
 */
class KeymapService {
    constructor(node, useCapture = false) {
        this.keymap = new Map();
        this.onKeyEvent = (keyEvent) => {
            const keyCode = KeyHelper.getKeyCode(keyEvent);
            const keyAssist = KeyHelper.getAssistKey(keyEvent);
            const keys = keyAssist + keyCode;
            this.run(keys, keyEvent);
        };
        this.onMouseEvent = (mouseEvent) => {
            const mouseCode = KeyHelper.getMouseCode(mouseEvent);
            const keyAssist = KeyHelper.getAssistKey(mouseEvent);
            const keys = keyAssist + mouseCode;
            this.run(keys, mouseEvent);
        };
        this.onWheelEvent = (wheelEvent) => {
            const wheelCode = KeyHelper.getWheelCode(wheelEvent);
            const keyAssist = KeyHelper.getAssistKey(wheelEvent);
            const keys = keyAssist + wheelCode;
            this.run(keys, wheelEvent);
        };
        this.sorter = (a, b) => { var _a, _b; return ((_a = b.weight) !== null && _a !== void 0 ? _a : 0) - ((_b = a.weight) !== null && _b !== void 0 ? _b : 0); };
        this.useCapture = useCapture;
        this.targetNode = node !== null && node !== void 0 ? node : document;
        this.targetNode.addEventListener('keydown', this.onKeyEvent, { capture: useCapture });
        this.targetNode.addEventListener('mousedown', this.onMouseEvent, { capture: useCapture });
        this.targetNode.addEventListener('wheel', this.onWheelEvent, { passive: false });
    }
    /**
     * 注册快捷键
     * @param keymap - 快捷键配置
     */
    register(keymap) {
        // 校验
        const isValid = this.isValidContributedKeyBinding(keymap);
        if (!isValid) {
            return;
        }
        const platform = PlatformUtil.isMac() || PlatformUtil.isIOS() ? 'mac' : 'win';
        const keyBindings = [].concat(keymap.keyBindings[platform]);
        keyBindings.forEach((value) => {
            if (value === KeyCode.EMPTY) {
                return;
            }
            let map = [keymap];
            if (this.keymap.has(value)) {
                const keymapValue = this.keymap.get(value);
                if (keymapValue) {
                    map = map.concat(keymapValue);
                }
            }
            this.keymap.set(value, map);
        });
    }
    /**
     * 根据 handler 来移除所有此 handler 注册的快捷键
     * @param handler - 需要移除的 handler
     */
    removeByFeature(handler) {
        const needDeleteKey = [];
        const needDeleteHandler = [];
        this.keymap.forEach((value, key) => {
            value.forEach((keymapItem) => {
                if (keymapItem.handler === handler) {
                    // 这里的 item 是一个数组 按需删除
                    if (value.length === 1) {
                        needDeleteKey.push(key);
                    }
                    else {
                        needDeleteHandler.push([key, keymapItem]);
                    }
                }
            });
        });
        needDeleteKey.forEach((key) => {
            this.keymap.delete(key);
        });
        needDeleteHandler.forEach((item) => {
            const [key, keymapItem] = item;
            const keys = this.keymap.get(key);
            this.keymap.set(key, keys.filter((keymap) => keymap.handler !== keymapItem.handler));
        });
    }
    destroy() {
        this.targetNode.removeEventListener('keydown', this.onKeyEvent, { capture: this.useCapture });
        this.targetNode.removeEventListener('mousedown', this.onMouseEvent, { capture: this.useCapture });
        this.targetNode.removeEventListener('wheel', this.onWheelEvent);
    }
    remove(keymap) {
        // 校验
        const isValid = this.isValidContributedKeyBinding(keymap);
        if (!isValid) {
            return;
        }
        const platform = PlatformUtil.isMac() || PlatformUtil.isIOS() ? 'mac' : 'win';
        const keyBindings = [].concat(keymap.keyBindings[platform]);
        keyBindings.forEach((value) => {
            if (value === KeyCode.EMPTY) {
                return;
            }
            // 判断有没有注册过这个按键的
            if (this.keymap.has(value)) {
                const map = this.keymap.get(value);
                const targetIndex = map.findIndex((originKeymap) => 
                // 判断 keyBindings 对象是否相同
                originKeymap.keyBindings.win.join('') === keymap.keyBindings.win.join('')
                    && originKeymap.keyBindings.mac.join('') === keymap.keyBindings.mac.join('')
                    // 判断 handler 函数是否相同
                    && originKeymap.handler === keymap.handler
                    // 判断 when 函数是否相同
                    && originKeymap.when === keymap.when
                    // 判断 shouldPreventDefault 函数是否相同
                    && originKeymap.shouldPreventDefault === keymap.shouldPreventDefault
                    // 判断 weight 是否相同
                    && originKeymap.weight === keymap.weight);
                if (targetIndex !== -1) {
                    const temp = map.concat();
                    temp.splice(targetIndex, 1);
                    this.keymap.set(value, temp);
                }
            }
        });
    }
    get(keyBinding) {
        let keys;
        if (typeof keyBinding === 'number') {
            keys = [keyBinding];
        }
        else {
            const platform = PlatformUtil.isMac() || PlatformUtil.isIOS() ? 'mac' : 'win';
            keys = keyBinding[platform];
        }
        return keys.reduce((acc, key) => { var _a; return acc.concat(...((_a = this.keymap.get(key)) !== null && _a !== void 0 ? _a : [])); }, []);
    }
    isValidContributedKeyBinding(keyBinding) {
        if (!keyBinding) {
            return false;
        }
        if (typeof keyBinding.handler !== 'function') {
            return false;
        }
        if (keyBinding.when && typeof keyBinding.when !== 'function') {
            return false;
        }
        if (keyBinding.keyBindings.mac && !Array.isArray(keyBinding.keyBindings.mac)) {
            return false;
        }
        if (keyBinding.keyBindings.win && !Array.isArray(keyBinding.keyBindings.win)) {
            return false;
        }
        if (keyBinding.description && !Array.isArray(keyBinding.description)) {
            return false;
        }
        return true;
    }
    /**
     * 执行快捷键
     * @param keyCode - 快捷键组合的键值
     * @param event - UI 事件
     */
    run(keyCode, event) {
        const keymapConfig = this.keymap.get(keyCode);
        keymapConfig === null || keymapConfig === void 0 ? void 0 : keymapConfig.sort(this.sorter);
        keymapConfig === null || keymapConfig === void 0 ? void 0 : keymapConfig.forEach((keymapConfigItem) => {
            const { handler, when, strict = true, shouldPreventDefault = true, } = keymapConfigItem;
            if (strict
                && (event instanceof KeyboardEvent)
                && (event.location === 1 || event.location === 2 || event.repeat)) {
                if (shouldPreventDefault)
                    event.preventDefault();
                return;
            }
            if ((when === null || when === void 0 ? void 0 : when(event)) || !when) {
                if (shouldPreventDefault)
                    event.preventDefault();
                handler(event);
            }
        });
    }
}

export { KeymapService, KeymapService as default };
//# sourceMappingURL=keymap-service.js.map
